import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Shield, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHero } from '../components/shared/PageHero';
import { useSEO } from '../hooks/useSEO';
import {
  ACCESSORY_CATEGORIES,
  FINDER_FAMILIES,
  PART_CATEGORIES,
  STORE_ACCESSORIES_URL,
  STORE_PARTS_URL
} from '../data/storeCatalog';

export default function AccessoriesParts() {
  const [selectedFamilyId, setSelectedFamilyId] = useState(FINDER_FAMILIES[0]?.id ?? '');
  const [selectedModelId, setSelectedModelId] = useState(FINDER_FAMILIES[0]?.models[0]?.id ?? '');
  const { hash } = useLocation();

  useSEO({
    title: 'Accesorios y recambios Zwheel',
    description: 'Encuentra accesorios para mejorar tu experiencia y recambios compatibles para tu modelo Zwheel.',
    keywords: 'accesorios Zwheel, recambios Zwheel, piezas compatibles, patinete electrico'
  });

  const selectedFamily = useMemo(
    () => FINDER_FAMILIES.find((family) => family.id === selectedFamilyId) ?? FINDER_FAMILIES[0],
    [selectedFamilyId]
  );

  useEffect(() => {
    if (!selectedFamily) {
      return;
    }

    setSelectedModelId(selectedFamily.models[0]?.id ?? '');
  }, [selectedFamily]);

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  const selectedModel = selectedFamily?.models.find((model) => model.id === selectedModelId) ?? selectedFamily?.models[0];
  const modelCount = selectedFamily?.models.length ?? 0;

  return (
    <div className="bg-white">
      <PageHero
        tag="Guia oficial"
        title="Accesorios y recambios"
        subtitle="El sitio oficial te ayuda a entrar en la tienda por la ruta correcta: accesorios por necesidad y recambios por compatibilidad."
      />

      <section className="py-20 bg-[#f4f0e8]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-12">
            <span className="text-sm font-bold tracking-[0.16em] uppercase text-primary block mb-3">
              Dos caminos claros
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-5">
              Una pagina para orientarte antes de comprar
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Aqui no listamos todo el catalogo. Te mostramos las puertas correctas de entrada para que llegues a la
              tienda con una intencion mucho mas clara.
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-6">
            <article className="group overflow-hidden rounded-[2rem] bg-white border border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.08)]">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src="/images/access/accesorios.jpg"
                  alt="Accesorios Zwheel"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white/90 backdrop-blur-sm mb-3">
                    Accesorios
                  </span>
                  <h3 className="text-3xl font-bold text-white mb-3">Mejora el uso diario</h3>
                  <p className="text-white/80 max-w-xl leading-relaxed">
                    Seguridad, personalizacion, ruedas y soluciones para guardar o transportar tu Zwheel con mas comodidad.
                  </p>
                </div>
              </div>
              <div className="p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <p className="text-gray-600 leading-relaxed max-w-xl">
                  Ideal para usuarios que ya saben que quieren ampliar la experiencia, pero todavia no necesitan entrar en un listado completo.
                </p>
                <Button asChild size="lg">
                  <a href={STORE_ACCESSORIES_URL} target="_blank" rel="noopener noreferrer">
                    Explorar accesorios
                  </a>
                </Button>
              </div>
            </article>

            <article className="group overflow-hidden rounded-[2rem] border border-white/8 bg-[#151515] text-white shadow-[0_30px_80px_rgba(0,0,0,0.16)]">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src="/images/access/recambios.jpg"
                  alt="Recambios Zwheel"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white/90 backdrop-blur-sm mb-3">
                    Recambios
                  </span>
                  <h3 className="text-3xl font-bold mb-3">Busca por compatibilidad</h3>
                  <p className="text-white/75 max-w-xl leading-relaxed">
                    Antes de comprar una pieza, valida familia, modelo y codigo cuando haga falta. Es la forma mas segura de reducir errores.
                  </p>
                </div>
              </div>
              <div className="border-t border-white/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.04)_0%,rgba(255,255,255,0.015)_100%)] p-7">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <p className="text-white/72 leading-relaxed max-w-xl">
                  Pensado para usuarios que necesitan llegar a la pieza correcta sin perderse entre variantes y codigos.
                  </p>
                  <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100">
                    <a href={STORE_PARTS_URL} target="_blank" rel="noopener noreferrer">
                      Abrir recambios
                    </a>
                  </Button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 xl:grid-cols-[0.88fr_1.12fr] gap-8 items-stretch">
            <div className="rounded-[2rem] overflow-hidden bg-[#111111] text-white min-h-[520px] flex flex-col">
              <div className="relative flex-1 min-h-[280px]">
                <img
                  src={selectedFamily?.image || '/placeholder-scooter.png'}
                  alt={selectedFamily?.name || 'Familia Zwheel'}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white/80 backdrop-blur-sm mb-3">
                    Compatibilidad guiada
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold mb-3">
                    Encuentra la ruta mas cercana a tu modelo
                  </h2>
                  <p className="text-white/75 leading-relaxed max-w-lg">
                    Usa esta guia antes de entrar en la tienda si no quieres navegar a ciegas entre piezas y variantes.
                  </p>
                </div>
              </div>

              <div className="p-6 border-t border-white/10">
                <div className="text-sm uppercase tracking-[0.16em] text-white/50 mb-2">Familia seleccionada</div>
                <div className="text-2xl font-bold mb-2">{selectedFamily?.name}</div>
                <p className="text-white/70 leading-relaxed">{selectedFamily?.description}</p>
              </div>
            </div>

            <div className="rounded-[2rem] border border-gray-200 bg-[#f8f6f2] p-8 md:p-10 shadow-[0_24px_80px_rgba(0,0,0,0.06)] flex h-full flex-col">
              <div className="flex items-center gap-3 mb-5">
                <Wrench className="w-5 h-5 text-primary" />
                <span className="text-sm font-bold tracking-[0.16em] uppercase text-gray-500">Buscador oficial</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Primero modelo, despues compra
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-2xl">
                Zwheel trabaja con familias, versiones y codigos. Selecciona tu serie y el modelo mas cercano para entrar en la tienda por una ruta mucho mas precisa.
              </p>

              <div className="rounded-[1.65rem] border border-gray-200 bg-white p-5 md:p-6 mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Selecciona tu familia y tu modelo</div>
                    <div className="text-sm text-gray-500">Usa esta ruta antes de entrar en la tienda para reducir errores.</div>
                  </div>
                  <div className="inline-flex w-fit rounded-full bg-[#f3eee6] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-gray-700">
                    {modelCount} modelos disponibles
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="text-sm font-semibold text-gray-700 block mb-3">Familia</span>
                    <select
                      value={selectedFamilyId}
                      onChange={(event) => setSelectedFamilyId(event.target.value)}
                      className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3.5 text-gray-900 outline-none focus:border-black"
                    >
                      {FINDER_FAMILIES.map((family) => (
                        <option key={family.id} value={family.id}>
                          {family.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-gray-700 block mb-3">Modelo</span>
                    <select
                      value={selectedModelId}
                      onChange={(event) => setSelectedModelId(event.target.value)}
                      className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3.5 text-gray-900 outline-none focus:border-black"
                    >
                      {selectedFamily?.models.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              {selectedFamily && (
                <div className="flex-1 min-h-[360px]">
                  <div className="rounded-[1.9rem] bg-white border border-gray-200 overflow-hidden shadow-[0_18px_55px_rgba(0,0,0,0.05)] h-full">
                    <div className="grid h-full grid-cols-1 lg:grid-cols-[0.9fr_1.1fr]">
                      <div className="relative min-h-[260px] lg:min-h-full">
                        <img
                          src={selectedFamily.image}
                          alt={selectedFamily.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5">
                          <div className="inline-flex rounded-full bg-black/35 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/85 backdrop-blur-sm mb-3">
                            Modelo seleccionado
                          </div>
                          <h3 className="text-3xl font-bold text-white">{selectedModel?.label}</h3>
                        </div>
                      </div>

                      <div className="p-6 md:p-8 flex flex-col h-full">
                        <div>
                          <div className="text-sm uppercase tracking-[0.16em] text-gray-500 mb-2">{selectedFamily.name}</div>
                          <h3 className="text-3xl font-bold text-gray-900 mb-3">{selectedModel?.label}</h3>
                          <p className="text-gray-600 leading-relaxed mb-6 text-lg">
                            {selectedModel?.description || selectedFamily.description}
                          </p>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                            <div className="rounded-[1.25rem] bg-[#f7f3ed] p-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-gray-500 mb-2">Familia</div>
                              <div className="text-lg font-semibold text-gray-900 mb-1">{selectedFamily.name}</div>
                              <p className="text-sm text-gray-600 leading-relaxed">{selectedFamily.description}</p>
                            </div>
                            <div className="rounded-[1.25rem] bg-[#f7f3ed] p-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-gray-500 mb-2">Ruta recomendada</div>
                              <div className="text-lg font-semibold text-gray-900 mb-1">{modelCount} variantes disponibles</div>
                              <p className="text-sm text-gray-600 leading-relaxed">
                                Selecciona la opcion mas cercana a tu modelo antes de abrir la tienda.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="mt-auto pt-6 border-t border-gray-200">
                          <div className="flex flex-col sm:flex-row gap-3">
                            <Button asChild>
                              <a href={selectedModel?.shopUrl || STORE_PARTS_URL} target="_blank" rel="noopener noreferrer">
                                Buscar en la tienda
                                <ArrowRight className="w-4 h-4" />
                              </a>
                            </Button>
                            <Button asChild variant="outline">
                              <Link to="/contacto">Necesito ayuda con mi modelo</Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="accessories" className="scroll-mt-24 border-y border-gray-100 bg-[#faf7f1] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-12">
            <span className="text-sm font-bold tracking-[0.16em] uppercase text-primary block mb-3">
              Accesorios
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-5">
              Explora por necesidad, no por ruido de catalogo
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Cada bloque funciona como una puerta de entrada. Luego la tienda se encarga del listado completo, el stock y la compra final.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {ACCESSORY_CATEGORIES.map((category) => (
              <article
                key={category.id}
                id={category.id}
                className="group scroll-mt-24 overflow-hidden rounded-[1.85rem] border border-black/5 bg-white shadow-[0_18px_60px_rgba(0,0,0,0.05)]"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5">
                    <div className="text-xs uppercase tracking-[0.16em] text-white/70 mb-2">Categoria</div>
                    <h3 className="text-2xl font-bold text-white">{category.name}</h3>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-gray-600 leading-relaxed mb-5">{category.description}</p>
                  <Button asChild variant="outline">
                    <a href={category.shopUrl} target="_blank" rel="noopener noreferrer">
                      Ver categoria
                    </a>
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="parts" className="scroll-mt-24 bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-12">
            <span className="text-sm font-bold tracking-[0.16em] uppercase text-primary block mb-3">
              Recambios
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-5">
              Entra por familia de pieza cuando ya sabes que necesitas
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Si tienes clara la zona del patinete que quieres revisar, estas tarjetas te llevan a una entrada mas afinada dentro de la tienda.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {PART_CATEGORIES.map((category) => (
              <article
                key={category.id}
                id={category.id}
                className="group scroll-mt-24 overflow-hidden rounded-[1.85rem] border border-gray-200 bg-[#fbfbfa] shadow-[0_16px_50px_rgba(0,0,0,0.04)]"
              >
                <div className="grid grid-cols-1 sm:grid-cols-[0.95fr_1.05fr]">
                  <div className="relative min-h-[220px]">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
                  </div>

                  <div className="p-6 flex flex-col">
                    <div className="text-xs uppercase tracking-[0.16em] text-gray-500 mb-2">Tipo de pieza</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{category.name}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">{category.description}</p>
                    <div className="mt-auto">
                      <Button asChild variant="outline">
                        <a href={category.shopUrl} target="_blank" rel="noopener noreferrer">
                          Ver categoria
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#111111]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] rounded-[2rem] overflow-hidden bg-black border border-white/10">
            <div className="relative min-h-[320px] xl:min-h-[100%]">
              <img
                src="/images/product/zrino/cover.png"
                alt="Ayuda oficial Zwheel"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/35 via-black/20 to-black/70" />
            </div>

            <div className="p-8 md:p-10 lg:p-12 text-white flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 text-white" />
                <span className="text-sm uppercase tracking-[0.16em] text-white/60">Ayuda oficial</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                No compres una pieza si no estas seguro de la compatibilidad
              </h2>
              <p className="text-white/75 text-lg leading-relaxed mb-8 max-w-xl">
                Si tu patinete incluye una variante o un codigo interno, habla con Zwheel antes de finalizar la compra. Es mejor validar una vez que pedir dos veces.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100">
                  <Link to="/contacto">Contactar con soporte</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white/10">
                  <a href={STORE_PARTS_URL} target="_blank" rel="noopener noreferrer">
                    Abrir tienda de recambios
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
